import React, { useState } from 'react'
import { makeStyles, Theme, useTheme } from '@material-ui/core/styles'
import { FormattedMessage, IntlShape, useIntl } from 'react-intl'
import messages from './messages'
import { productDetailRoute } from '@src/routes'
import { HandleMarkup } from '@src/components/HandleMarkup'
import { Link } from '@src/components/Link'
import DownloadIcon from '@src/icons/DownloadIcon'
import SDSLink from '@src/components/SDSLink'
import clsx from 'clsx'
import SearchIcon from '@src/icons/SearchIcon'
import { CartItemInput, CertificateType } from '@src/types/graphql-types'
import { CertificateOfAnalysisDocument } from '@src/queries/CertificateOfAnalysisQuery.generated'
import { MaterialDetailFragment } from '@src/queries/MaterialDetailQuery.generated'
import { useSdsCertificateSearchQuery } from '@src/queries/SdsCertificateSearchQuery.generated'
import { ValidMaterialPricingDetailFragment } from '@src/fragments/ProductPricing.generated'
import {
  CertificateFormProps,
  handleCertificatePdf,
  showCertificate,
} from '../ProductDetail/ProductDocumentation/CertificateForm'
import { UserSession } from '@src/utils/userSession'
import { ApolloClient } from 'apollo-boost'
import { useApolloClient } from 'react-apollo'
import { useUserSession } from '@src/utils/useUserSession'
import { useGlobalSnackbar } from '@src/components/GlobalSnackbar/globalSnackBarContext'
import getConfig from 'next/config'
import { Button, useMediaQuery, Typography } from '@material-ui/core'
import LinkStyleButton from '@src/components/LinkStyleButton'
import { useAddToCartWithGlobalSnackbar } from '@src/utils/useCart'
import { ScanNowMaterial } from '@utils/analytics/types'
import { setAddToCartData } from '@utils/analytics/cartAndCheckoutEvents'
import {
  AbbreviatedQmDocumentType,
  AddToCartPagesEnum,
  QmDocumentType,
} from '@utils/analytics/enums'
import CircleLoader from '@src/components/CircleLoader'

const useStyles = makeStyles((theme: Theme) => ({
  resultsContainer: {
    padding: '5px 0 40px',
  },
  results: {
    textTransform: 'uppercase',
  },
  materialInfo: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: theme.spacing(6),
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
    },
  },
  imageContainer: {
    width: '100%',
    [theme.breakpoints.up('md')]: {
      width: '180px',
      marginRight: '40px',
    },
  },
  materialImage: {
    width: '100%',
  },
  productIdLink: {
    fontSize: theme.typography.pxToRem(14),
    lineHeight: '20px',
    fontWeight: 900,
  },
  materialNameLink: {
    fontSize: theme.typography.pxToRem(18),
    letterSpacing: '0.18px',
    lineHeight: '22px',
    fontWeight: 900,
  },
  materialDetails: {
    display: 'flex',
    flexDirection: 'column',
  },
  documentLinks: {
    display: 'flex',
    paddingTop: '16px',
    fontSize: theme.typography.pxToRem(16),
    flexDirection: 'column',
    '& > button, > span , & > a': {
      fontSize: theme.typography.pxToRem(16),
      fontWeight: 900,
      width: 'fit-content',
    },
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
      '& > button, > span , & > a': {
        paddingRight: '16px',
      },
    },
  },
  documentLink: {
    paddingTop: '10px',
  },
  triggerLink: {
    color: theme.palette.primary.main,
    fontWeight: 900,
    textDecoration: 'none',
    '&:hover': {
      cursor: 'pointer',
    },
  },
  linkContent: {
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
  },
  linkIconContainer: {
    display: 'flex',
    fontSize: theme.typography.pxToRem(16),
    marginRight: theme.spacing(1),
  },
  linkIcon: {
    marginRight: theme.spacing(1),
  },
  searchIcon: {
    fontSize: theme.typography.pxToRem(12),
    marginLeft: theme.spacing(1 / 2),
  },
  addToCartButton: {
    width: '100%',
    fontWeight: 700,
    lineHeight: 1.5,
    fontSize: theme.typography.pxToRem(16),
    height: '48px',
    marginTop: theme.spacing(5),
    padding: theme.spacing(2, 0),
    [theme.breakpoints.up('md')]: {
      position: 'relative',
      width: '144px',
      height: '40px',
      lineHeight: '24px',
    },
  },
}))

interface ScanNowMaterialInfoProps {
  material: MaterialDetailFragment
  lotNumber: string
  isBlueProduct: boolean
  certificateContent: any
  materialPricing: ValidMaterialPricingDetailFragment[]
}

const ScanNowMaterialInfo: React.FC<ScanNowMaterialInfoProps> = ({
  material,
  lotNumber,
  isBlueProduct,
  certificateContent,
  materialPricing,
}) => {
  const { product, number, name, description, image, brand } = material
  const classes = useStyles()
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md')) === true

  const client = useApolloClient()
  const intl = useIntl()
  const { setGlobalSnackbarState } = useGlobalSnackbar()
  const [pdfLoading, setPDFLoading] = useState(false)
  const { userSession } = useUserSession()

  const isCOA =
    certificateContent?.documentType === QmDocumentType.CofAnalysisDoc

  const handleGetCertificate = (
    client: ApolloClient<any>,
    props: CertificateFormProps,
    intl: IntlShape,
    userSession: UserSession,
    setGlobalSnackbarState: any,
    setPDFLoading?: any
  ) => {
    const documentType = AbbreviatedQmDocumentType[props.type]
    const queryValues = {
      productNumber: product,
      lotNumber: lotNumber?.trim(),
      brand: brand.key,
      documentType,
      sku: number,
    }
    const queryOptions = {
      query: CertificateOfAnalysisDocument,
      variables: queryValues,
    }

    const dataAccessor = isCOA
      ? 'getCertificateOfAnalysis'
      : 'getCertificateOfOrigin'

    props.isBlueProduct
      ? handleCertificatePdf(
          client,
          props,
          queryValues,
          intl,
          setGlobalSnackbarState,
          setPDFLoading
        )
      : showCertificate(
          client,
          dataAccessor,
          intl,
          setGlobalSnackbarState,
          isCOA,
          undefined,
          queryOptions,
          userSession,
          props,
          setPDFLoading
        )
  }
  const {
    publicRuntimeConfig: {
      featureFlags: { blueDocsManagement },
    },
  } = getConfig()
  const { source, unit, owner } = certificateContent?.text || {}

  const { data: sdsData, loading: sdsLoading } = useSdsCertificateSearchQuery({
    variables: { productNumber: product, brand: brand.key },
    ssr: false,
  })

  const sds = sdsData?.getSdsCertificateSearch?.sds ?? []
  const sdsLanguages = sdsLoading
    ? []
    : (sds.length && sds[0]?.languages?.altLanguages) || []

  const showSkuInput =
    blueDocsManagement && unit === 'CHEM' && owner === 'MDA' && source === 'GDI'

  const props = {
    showSkuInput,
    type: isCOA ? CertificateType.Coa : CertificateType.Coq,
    productNumber: product,
    title: '',
    isBlueProduct,
    text: { unit, owner },
  }
  const addToCart = useAddToCartWithGlobalSnackbar()
  const [isCartUpdating, setIsCartUpdating] = useState(false)

  return (
    <div className={classes.resultsContainer}>
      <Typography variant="h2" component="h2" className={classes.results}>
        <FormattedMessage {...messages.RESULTS} />
      </Typography>
      <div className={classes.materialInfo}>
        {isDesktop && (
          <div className={classes.imageContainer}>
            <img
              className={classes.materialImage}
              src={image.mediumUrl}
              alt={image.altText || ''}
            />
          </div>
        )}
        <div className={classes.materialDetails}>
          <Link
            {...productDetailRoute.index(
              brand.key,
              product,
              {},
              'product-documentation'
            )}
            passHref
          >
            {number}
          </Link>
          <Link {...productDetailRoute.index(brand.key, product)} passHref>
            <a className={classes.materialNameLink}>
              <HandleMarkup value={name} />
            </a>
          </Link>
          <HandleMarkup value={description} />
          <div className={classes.documentLinks}>
            <SDSLink
              sdsLanguages={sdsLanguages}
              productNumber={product}
              brandKey={brand.key}
              color="primary"
              className={classes.documentLink}
              trigger={
                <span className={classes.linkContent}>
                  <span className={classes.linkIconContainer}>
                    <DownloadIcon />
                  </span>
                  <FormattedMessage {...messages.SDS} />
                </span>
              }
            />
            {!!lotNumber && (
              <LinkStyleButton
                className={clsx(classes.documentLink)}
                onClick={() => {
                  handleGetCertificate(
                    client,
                    props,
                    intl,
                    userSession,
                    setGlobalSnackbarState,
                    setPDFLoading
                  )
                }}
              >
                <SearchIcon
                  className={clsx(classes.linkIcon, classes.searchIcon)}
                />
                <FormattedMessage
                  {...messages.DOCUMENT_FOR_LOT_NUMBER}
                  values={{
                    docType: isCOA ? 'CoA' : 'CoQ',
                    lotNumber,
                  }}
                />
              </LinkStyleButton>
            )}
          </div>
          {!isDesktop && (
            <div className={classes.imageContainer}>
              <img
                className={classes.materialImage}
                src={image.largeUrl}
                alt={image.altText || ''}
              />
            </div>
          )}
          <Button
            color="secondary"
            variant="contained"
            size="small"
            disabled={isCartUpdating}
            className={classes.addToCartButton}
            onClick={async () => {
              setIsCartUpdating(true)
              const cartItems: CartItemInput[] = [
                {
                  materialNumber: number,
                  quantity: 1,
                },
              ]
              const addtoCartItem: ScanNowMaterial[] = [
                {
                  materialNumber: number,
                  quantity: 1,
                  brand: brand.key,
                  materialDescription: name || '',
                  product: product,
                },
              ]
              setAddToCartData(
                cartItems,
                materialPricing?.length ? materialPricing : addtoCartItem,
                AddToCartPagesEnum.ScanNowPage
              )
              await addToCart(cartItems)
              setIsCartUpdating(false)
            }}
          >
            <FormattedMessage id="ADD_TO_CART" defaultMessage="Add to Cart" />
          </Button>
        </div>
      </div>
      {pdfLoading ? <CircleLoader /> : null}
    </div>
  )
}

export default ScanNowMaterialInfo
